import { useMemo } from "react";
import { InputCheckbox, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { AirlineGroup, AirlineOption } from "../../../types";
import { SearchablePicker, type SearchablePickerOption } from "../../SearchablePicker";
import styles from "./AccessSelector.module.css";

type GroupAccessSectionProps = {
  groups: AirlineGroup[];
  airlines: AirlineOption[];
  selectedGroupId: number | null;
  selectedAirlineIds: number[];
  onGroupChange: (groupId: number) => void;
  onAirlineToggle: (airlineId: number) => void;
};

export const GroupAccessSection = ({
  groups,
  airlines,
  selectedGroupId,
  selectedAirlineIds,
  onGroupChange,
  onAirlineToggle,
}: GroupAccessSectionProps) => {
  const i18n = useI18n();
  const groupOptions: SearchablePickerOption[] = useMemo(
    () => groups.map((group) => ({ id: String(group.id), label: group.name })),
    [groups]
  );

  const selectedGroup = useMemo(() => groups.find((group) => group.id === selectedGroupId) ?? null, [groups, selectedGroupId]);

  const filteredAirlines = useMemo(() => {
    if (!selectedGroup) return [];
    return airlines.filter((airline) => selectedGroup.airlineCodes.includes(airline.code));
  }, [airlines, selectedGroup]);

  return (
    <Stack gap={4}>
      <SearchablePicker
        layout="fluid"
        label={i18n.trans(t("flights_airline_portal_add_user_group_selector_label"))}
        placeholder={i18n.trans(t("flights_airline_portal_add_user_group_airline_selector_placeholder"))}
        searchPlaceholder={i18n.trans(t("flights_airline_portal_add_user_group_airline_selector_search_placeholder"))}
        options={groupOptions}
        value={selectedGroupId === null ? null : String(selectedGroupId)}
        onChange={(id) => onGroupChange(Number(id))}
      />

      {filteredAirlines.length > 0 && (
        <Stack gap={2}>
          <Text variant="emphasized_2">{i18n.trans(t("flights_airline_portal_add_user_group_airline_selector_label"))}</Text>
          <div className={styles.airlineCheckboxes}>
            {filteredAirlines.map((airline) => (
              <InputCheckbox
                key={airline.id}
                name={`airline-${airline.id}`}
                label={`${airline.code} — ${airline.name}`}
                checked={selectedAirlineIds.includes(airline.id)}
                onChange={() => onAirlineToggle(airline.id)}
              />
            ))}
          </div>
        </Stack>
      )}
    </Stack>
  );
};
